import { useQuery } from '@base-app/web2'
import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ALL_APPS_QUERY, APPS_QUERY } from '../../../graphql/applications/queries'
import type { Application } from '../../../graphql/applications/types'
import { CLIENTS_QUERY } from '../../../graphql/clients/queries'
import { Button, Select, type SelectOption } from '../../atoms'

interface AppSelectorProps {
  selectedClientId?: string
  selectedAppId?: string
  // Global admins pick a client, then one of its apps. Everyone else picks among the apps
  // they belong to (the API returns only those), and the client comes from the app.
  isAdmin: boolean
  onApply: (
    client: { id: string; name: string },
    // The application as the API returned it for this viewer (accesses, viewerPermissions, ...)
    app: Application,
  ) => void
  onClear: () => void
}

export const AppSelector = memo(
  ({ selectedClientId, selectedAppId, isAdmin, onApply, onClear }: AppSelectorProps) => {
    const { t } = useTranslation('admin-mobifull')
    const [pendingClientId, setPendingClientId] = useState(selectedClientId ?? '')
    const [pendingAppId, setPendingAppId] = useState(selectedAppId ?? '')

    // `clients` is admin-only on the API, so members never ask for it
    const { data: clientsData } = useQuery(CLIENTS_QUERY, {
      variables: { limit: 999 },
      skip: !isAdmin,
    })
    const clients = clientsData?.clients ?? []

    const { data: clientAppsData } = useQuery(APPS_QUERY, {
      variables: { clientId: pendingClientId, limit: 999 },
      skip: !isAdmin || !pendingClientId,
    })
    const { data: memberAppsData } = useQuery(ALL_APPS_QUERY, { skip: isAdmin })
    const apps = (isAdmin ? clientAppsData : memberAppsData)?.applications ?? []

    // A member of exactly one app gets it selected right away, so its sections show up
    const onlyMemberApp = !isAdmin && apps.length === 1 ? apps[0] : null
    useEffect(() => {
      if (onlyMemberApp && !selectedAppId) {
        setPendingAppId(onlyMemberApp.id)
        onApply({ id: onlyMemberApp.client_id, name: '' }, onlyMemberApp)
      }
    }, [onlyMemberApp, selectedAppId, onApply])

    const clientOptions: SelectOption[] = useMemo(
      () => [
        { value: '', label: t('sidebar.selectClient') },
        ...clients.map(c => ({ value: c.id, label: c.name })),
      ],
      [clients, t],
    )

    const appOptions: SelectOption[] = useMemo(
      () => [
        { value: '', label: t('sidebar.selectApp') },
        ...apps.map(a => ({ value: a.id, label: a.name })),
      ],
      [apps, t],
    )

    const handleClientChange = (clientId: string) => {
      setPendingClientId(clientId)
      setPendingAppId('')
    }

    const handleApply = () => {
      const app = apps.find(a => a.id === pendingAppId)
      if (!app) {
        return
      }
      if (!isAdmin) {
        // Members can't read client names; only the id is used to build section URLs
        onApply({ id: app.client_id, name: '' }, app)
        return
      }
      const client = clients.find(c => c.id === pendingClientId)
      if (client) {
        onApply({ id: client.id, name: client.name }, app)
      }
    }

    const handleClear = () => {
      setPendingClientId('')
      setPendingAppId('')
      onClear()
    }

    return (
      <div className="sidebar__app-select">
        <div className="sidebar__app-select-fields">
          {isAdmin && (
            <Select
              value={pendingClientId}
              onChange={handleClientChange}
              options={clientOptions}
              aria-label={t('sidebar.selectClient')}
            />
          )}
          <Select
            value={pendingAppId}
            onChange={setPendingAppId}
            options={appOptions}
            disabled={isAdmin && !pendingClientId}
            aria-label={t('sidebar.selectApp')}
          />
        </div>
        <div className="sidebar__app-actions">
          <Button
            onClick={handleApply}
            disabled={(isAdmin && !pendingClientId) || !pendingAppId}
            variant="primary"
            className="sidebar__app-apply">
            {t('sidebar.apply')}
          </Button>
          <Button onClick={handleClear} variant="ghost" className="sidebar__app-clear">
            {t('sidebar.clear')}
          </Button>
        </div>
      </div>
    )
  },
)

AppSelector.displayName = 'AppSelector'
